import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion'
import AccordionItem, { DefaultAccordionProviderOptions } from '../../components/common/AccordionItem/AccordionItem'
import { GetElsLookupsState } from '../../redux/reducers/getLookups/getElsLookupsReducer';
import { getSummaryDataState } from '../../redux/reducers/getSummaryData/summaryDataReducer';
import { selectELSLookups, selectSummaryData } from '../../redux/selectors/exportSelectorPaths';
import { commaSeparate, formatCurrency } from '../../utils/formatter';
import { groupBy } from '../../utils/groupBy';
import './Summary.css'
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';

export default function UnitSummary(): JSX.Element {

    const summaryData: getSummaryDataState = useSelector(selectSummaryData);
    const [unitUseCount, setUnitUseCount] = useState<Partial<Record<string, Record<string, unknown>[]>>>({})
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const currencySymbol = "$";
    const simplifiedUnitSubtypeLookup = elsLookupsState.dropdowns && ((elsLookupsState.dropdowns.unitSubType1 !== undefined ? elsLookupsState.dropdowns.unitSubType1 as Record<string, unknown>[] : []).reduce((simplifiedLookup, unitSubtype) =>
        ({ [unitSubtype["unitSubtypeCode"] as string]: unitSubtype["unitSubtypeDescription"] as string, ...simplifiedLookup }), { "undefined": "" }));
    const { t, i18n } = useTranslation(['guided_wizard', 'summary']);
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const [vacantUnitCount, setVacantUnitCount] = useState(0)

    const formatRent = (value: number) => currencySymbol + formatCurrency(value, i18n.language)

    useEffect(() => {
        if (!summaryData.loading && summaryData.success) {
            if ((summaryData.data as Record<string, unknown>)["tenant_summary"]) {
                let count = 0
                const tempData = ((summaryData.data as Record<string, unknown>)["tenant_summary"] as Record<string, unknown>[]).filter(e => {
                    if (e.leaseStatusCode === "1") {
                        count += 1
                    }
                    return e.leaseStatusCode !== "1"
                })
                setUnitUseCount(groupBy(tempData, ({ unitSubtypeCode }) => unitSubtypeCode as string));
                setVacantUnitCount(count)
            }
        }
    }, [summaryData]);

    const getRentRange = (): Record<string, number> => {
        const rangeRecord: Record<string, number> = {
            "minRent": -1,
            "maxRent": -1
        }
        Object.keys(unitUseCount).forEach((key) => {
            rangeRecord[key + "MinRent"] = -1;
            rangeRecord[key + "MaxRent"] = -1;
            (unitUseCount[key] as Record<string, unknown>[]).forEach((unit) => {
                if (unit["currentBaseRent"] && unit["leaseStatusCode"] !== "1") {
                    if ((unit["currentBaseRent"] as number) < rangeRecord[key + "MinRent"] || rangeRecord[key + "MinRent"] === -1) {
                        rangeRecord[key + "MinRent"] = (unit["currentBaseRent"] as number);
                        if ((unit["currentBaseRent"] as number) < rangeRecord["minRent"] || rangeRecord["minRent"] === -1) {
                            rangeRecord["minRent"] = unit["currentBaseRent"] as number;
                        }
                    }
                    if ((unit["currentBaseRent"] as number) > rangeRecord[key + "MaxRent"]) {
                        rangeRecord[key + "MaxRent"] = (unit["currentBaseRent"] as number);
                        if ((unit["currentBaseRent"] as number) > rangeRecord["maxRent"]) {
                            rangeRecord["maxRent"] = unit["currentBaseRent"] as number;
                        }
                    }
                }
            });
        })
        return rangeRecord;
    }

    const buildUnitSummary = () => {
        const unitSummary = [];
        unitSummary.push(<div className='summary-section-container' key='unit-summary-unit-count'>
            <table className='summary-table' role='presentation'>
                <thead>
                    <tr>
                        <th>{t("unit-heading.unit-count", { ns: 'guided_wizard' })}</th>
                        <td>{commaSeparate(((summaryData.data as Record<string, unknown>)["tenant_summary"] as Record<string, unknown>[]).length)}</td>
                    </tr>
                </thead>
                <tbody>
                    {Object.keys(unitUseCount)
                    .filter(key => key !== "null")
                    .map((key, index) => (
                        <tr key={`unit-use-unit-count-${index})`}>
                            <td>
                                {key === "-" ? "-" : simplifiedUnitSubtypeLookup[key] as string}
                            </td>
                            <td>
                                {key === "-" ? "-" : commaSeparate(unitUseCount[key] ? unitUseCount[key]?.length : 0)}
                            </td>
                        </tr>
                    ))}
                    {vacantUnitCount > 0 &&
                        <tr key={`vacant-unit-count`}>
                            <td>
                                {t("vacant", { ns: "summary" })}
                            </td>
                            <td>
                                {commaSeparate(vacantUnitCount)}
                            </td>
                        </tr>
                    }
                    {unitUseCount["null"] &&
                        <tr key={"blank-unit-count"}>
                            <td>
                                {simplifiedUnitSubtypeLookup["null"] as string}
                            </td>
                            <td>
                                {commaSeparate(unitUseCount["null"] ? unitUseCount["null"].length : 0)}
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>);
        const rangeRecord = getRentRange();
        unitSummary.push(<div className='summary-section-container' key='unit-summary-rent-range'>
            <table className='summary-table' role='presentation'>
                <thead>
                    <tr>
                        <th>{t("unit-heading.total-rent-range", { ns: 'guided_wizard' })}</th>
                        <td>{rangeRecord["minRent"] === rangeRecord["maxRent"] ? (rangeRecord["minRent"] === -1 ? "" : formatRent(rangeRecord["minRent"])) : (formatRent(rangeRecord["minRent"]) + " - " + formatRent(rangeRecord["maxRent"]))}</td>
                    </tr>
                </thead>
                <tbody>
                    {Object.keys(unitUseCount).map((key, index) => (
                        <tr key={`unit-use-rent-range-${index})`}>
                            <td>
                                {key === "-" ? "-" : simplifiedUnitSubtypeLookup[key] as string}
                            </td>
                            <td>
                                {key === "-" ? "-" :rangeRecord[key + "MinRent"] === rangeRecord[key + "MaxRent"] ? (rangeRecord[key + "MinRent"] === -1 ? "" : formatRent(rangeRecord[key + "MinRent"])) : (formatRent(rangeRecord[key + "MinRent"]) + " - " + formatRent(rangeRecord[key + "MaxRent"]))}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>);
        return unitSummary;
    }

    return (
        <>
            <LoadingSpinner loading={elsLookupsState.loading}>
                {(Object.keys(unitUseCount).length > 0 || vacantUnitCount > 0) &&
                    <ControlledAccordion providerValue={providerValue}>
                        <AccordionItem header={t("commercial", { ns: 'summary' })}
                            key="unitSummary"
                            itemKey="unitSummary"
                            headingLevel={2}>
                            <div className='summary-grid'>{buildUnitSummary()}</div>
                        </AccordionItem>
                    </ControlledAccordion>
                }
            </LoadingSpinner>
        </>
    )
}